import { Avatar, Box, Button, Toolbar, Typography} from "@mui/material";
import { Theme } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import { matchPath, useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
// import GlobalSearch from "../components/GlobalSearch";
import "../styles/common.css"
import React, { useRef, useState, useEffect } from "react";
import CustomDatePicker, { CustomDatePickerHandle } from "../components/CustomDatePicker/CustomDatePicker";
import KeywordSearch from "../components/Reporting/KeywordSearch";
import useGlobalContext from "../context/useGlobalContext";
import ProgressSpinner from "../components/Common/ProgressSpinner";
import AppNotification from "../components/Common/AppNotification";
import TaskNotifications from "../components/ActionItems/TaskNotifications";
import { useNotificationCount } from "../hooks/useNotificationCount";
import { getAvatarColor, getAvatarInitials } from "../utils/utils";
import CloseIcon from "@mui/icons-material/Close";
import ClientAsk7G from "../components/ClientOverview/ClientAsk7G";

const useStyles = makeStyles((theme: Theme) => ({
  toolbarWrapper: {
    paddingLeft: "0px !important",
    paddingRight: "64px !important",
    [theme.breakpoints.down("lg")]: {
      paddingLeft: "0px !important",
      paddingRight: "54px !important",
    },
  },
  logoImg: {
    width: "178px",
    height: "44px",
    cursor: "pointer",
    [theme.breakpoints.down("lg")]: {
      width: "178px",
      height: "44px",
    },
  },
  userAvatar: {
    width: "40px",
    height: "40px",
    [theme.breakpoints.down("lg")]: {
      width: "36px",
      height: "36px",
    },
    cursor: "pointer",
    marginLeft: "10px",
    marginTop: "5px",
    borderRadius: "50%",
    transition: "box-shadow 0.3s ease-in-out",
  
    "&:hover": {
      boxShadow: "0 0 0 4px rgba(0, 0, 0, 0.1)", // subtle glow ring
    },
  },  
  defaultContainer: {
    // todo: large desktop has 46px, add laptop screen resolution before laptop
    marginLeft: "40px",
    [theme.breakpoints.down("lg")]: {
      marginLeft: "35px",
    },
  },
  containerWrapper: {
    // todo: large desktop has 46px, add laptop screen resolution before laptop
    marginRight: "0px",
    paddingRight: "24px",
    [theme.breakpoints.down("lg")]: {
      marginRight: "3px",
      paddingRight: "20px"
    },
  },
  childrenContainer: {
    // todo: large desktop has 46px, add laptop screen resolution before laptop
    marginTop: "40px",
    paddingBottom: "10px",
    [theme.breakpoints.down("lg")]: {
      marginTop: "35px",
      paddingBottom: "8px",
    },
  },
  rightSectionContent: {
    marginLeft: "28px",
    marginRight: "28px",
    // todo: large desktop has 46px, add laptop screen resolution before laptop
    marginTop: "40px",
    [theme.breakpoints.down("lg")]: {
      marginLeft: "20px",
      marginRight: "20px",
      marginTop: "35px",
    },
  },
  clientChildrenContainer: {
    marginTop: "0px !important",
  },
}));

interface Props {
  children: React.ReactNode;
}
const DefaultLayout: React.FC<Props> = ({ children }) => {
  const classes = useStyles();
  const location = useLocation();
  const {pathname} = useLocation();
  const isReportingPage = location.pathname.includes("/reporting");
  const { featureMap, accountDetails, impersonationDetails } = useGlobalContext();
  const isAskSevenGFlagEnalbled = featureMap?.ask_7g_page === true
  const [_switchState, setSwitchState] = useState<boolean>(false);
  const [showSpinner, _setShowSpinner] = useState(false);
  const datePickerRef = useRef<CustomDatePickerHandle>(null);
  const isExternalUser = accountDetails?.isExternalUser ?? false;
  const isMeetingDetailsPage = !!matchPath({ path: "/meeting/:id" }, pathname);
  const userProfileOrgId =  accountDetails?.profileOrgId ?? "";
  const isNotificationsEnabled = featureMap?.in_app_notifications === true;
  const userNotification = useNotificationCount(userProfileOrgId, isNotificationsEnabled);
  const isImpersonating = impersonationDetails?.isImpersonating === true;
  const impersonatingEmail = impersonationDetails?.impersonatingUserEmail;
  const { setImpersonationDetails } = useGlobalContext();
  const Ask7GEnabled = featureMap?.business_ask7g === true;
  const [isAsk7GExpanded, setIsAsk7GExpanded] = useState(false);

  useEffect(() => {
    setIsAsk7GExpanded(false);
  }, [location.pathname, location.search]);

  const handleStopImpersonation = () => {
    setImpersonationDetails(null);
    sessionStorage.removeItem("impersonationDetails");
    window.location.reload();
  };

  return (
    <>
      {isReportingPage &&
      <Box
        className="appHeader"
        sx={{
          position: "fixed",
          display: "block",
          background: "#FFFFFF",
          zIndex: 2,
          boxShadow: "0px -6px 50px 0px #0000000D",
          width: "calc(100% - 60px)",
          height: "72px",
          top: 0,
          left: "60px",
          borderBottom: "1px solid #E5E5E5",
        }}
      >
        <Toolbar
          className={classes.toolbarWrapper}
          sx={{
            height: "100%",
            justifyContent: isExternalUser ? "flex-end" : "space-between",
          }}
        >
          {!isExternalUser && (
          <>
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            {
            isReportingPage && !isAskSevenGFlagEnalbled && (
            <>
            <Typography variant="h4" sx={{ paddingLeft:6}}>
              Find the interactions where
            </Typography>
            <KeywordSearch /> 
            <Typography variant="h4" >
              was discussed
            </Typography>
            </>
          )}

            {/* Right-aligned Date Picker */}
            <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center", pr: 3 }}>
              <CustomDatePicker ref={datePickerRef} />
            </Box>
            <div className="ml-auto flex flex-row items-center gap-3">
            {isNotificationsEnabled && (
              <AppNotification
              notificationCount= {userNotification.notificationCount}
              notificationApiRefetch = {userNotification.getNotificationCountRefetch}
              contentComponent={<TaskNotifications userNotification={userNotification}/>}
              />
            )}
            {Ask7GEnabled && (
              <ClientAsk7G
                view="icon"
                isExpanded={isAsk7GExpanded}
                onToggleExpand={() => setIsAsk7GExpanded(!isAsk7GExpanded)}
              />
            )}
            </div>
          </Box>
          </>
          )}
        </Toolbar>
      </Box>
      }

      {isImpersonating && (
        <Box className="impersonation-banner">
          <Box className="impersonation-banner-left">
            <Typography className="impersonation-banner-label">
              IMPERSONATING
            </Typography>

            <Box className="impersonation-divider" />

            <Avatar 
              className="impersonation-avatar"
              sx={{bgcolor: getAvatarColor(impersonatingEmail),}}
            >
              {getAvatarInitials(impersonatingEmail)}
            </Avatar>

            <Typography className="impersonation-banner-email">
              {impersonationDetails?.impersonatingUserEmail}
            </Typography>
          </Box>

          <Button
            className="impersonation-stop-btn"
            size="small"
            onClick={handleStopImpersonation}
          >
            <CloseIcon className="impersonation-stop-btn-top-bar"/>
            Stop Impersonating
          </Button>
        </Box>
      )}
      <div
        className="appContent"
        style={{
          position: "relative",
          overflowY: "auto",
          display: "flex",
          flexDirection: "row",
          height: !isReportingPage 
            ? "100vh" 
            : isImpersonating 
              ? "calc(100vh - 35px)" 
              : "calc(100vh - 60px)",
          background: "#F8F8F8",
          top: isImpersonating ? "35px" :isMeetingDetailsPage ? "0px" : !isReportingPage ? "0px" : "60px",
        }}
      >
        <div
          id="sideparWrapper"
          style={{
            height: "100vh",
            width: "70px",
            flexShrink: 0,
            overflow: "hidden",
            borderRight: "1px solid #E5E5E5",
            background: "#FFFFFF",
            position: "fixed", // Make it fixed to align with the top bar
            top: 0, // Ensure it starts from the top
            bottom: 0,
            left: 0, // Align with the left side
            zIndex: 3, // Ensure it stays above other content if needed
          }}
        >
          <Sidebar setSwitchState={setSwitchState} />
        </div>
        <div
          id="containerParent"
          style={{
            flex: 7,
            flexShrink: 1,
            width: "calc(100% - 60px)", // Adjust width to account for the sidebar
            marginLeft: isMeetingDetailsPage ? "70px": "92px", // Ensure content does not overlap with the fixed sidebar
            marginTop: isImpersonating ? "10px" : !isReportingPage ? "0px" : "8.8px",
            overflowX: "hidden",
            scrollbarWidth: "thin",
            backgroundColor: isMeetingDetailsPage ? "#F8F8F8" : ""
          }}
          className={`${classes.defaultContainer} ${classes.containerWrapper}`}
        >
          <Box
          className={`${classes.childrenContainer} ${
            !isReportingPage ? classes.clientChildrenContainer : ""
          }`}
        >
          {Ask7GEnabled && (
            <Box sx={{ 
              width: "100%", 
              mt: isAsk7GExpanded ? "-20px" : 0,
              display: isAsk7GExpanded ? "block" : "none",
            }}>
              <ClientAsk7G
                view="full"
                isExpanded={isAsk7GExpanded}
                onToggleExpand={() => setIsAsk7GExpanded(false)}
              />
            </Box>
          )}
          <Box sx={{ display: isAsk7GExpanded ? "none" : "block", width: "100%" }}>
            {children}
          </Box>
        </Box>
        </div>
      </div>
      <ProgressSpinner showSpinner={showSpinner} />
    </>
  );
};

export default DefaultLayout;